import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const LS_KEY = "natur.accounting.activeSectionId";

/**
 * V1.38 · Hook central para gestionar la sección contable activa.
 * - Persiste el `section_id` seleccionado en localStorage.
 * - Carga la lista de secciones desde el backend al montar.
 * - Al primer arranque (o si el id guardado no existe) selecciona la
 *   sección por defecto (semilla "01 · General").
 */
export function useAccountingSections() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveIdState] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/sections");
      const items = data?.items || [];
      setSections(items);
      // Resolver id activo
      let next = activeId;
      const known = items.some((s) => s.id === next);
      if (!known) {
        // Escoge la sección activa con menor "order" (o la primera)
        const active = items.filter((s) => s.active !== false);
        next = (active[0] || items[0])?.id || "";
      }
      if (next !== activeId) setActiveIdInternal(next);
    } finally {
      setLoading(false);
    }
   
  }, []);

  useEffect(() => { load(); }, [load]);

  const setActiveIdInternal = (id) => {
    setActiveIdState(id);
    try { localStorage.setItem(LS_KEY, id || ""); } catch { /* ignore */ }
  };

  const setActiveId = useCallback((id) => setActiveIdInternal(id), []);

  const active = sections.find((s) => s.id === activeId) || null;

  return { sections, active, activeId, setActiveId, loading, reload: load };
}
